import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
} from 'antd';
import { SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { getInvites, getInviteSummary } from '../../api/invites';
import { useAuth } from '../../store/authContext';
import { canManageInvitePolicy } from './policyForm';

const statusMap = {
  REGISTERED: { label: '已注册', color: 'default' },
  PENDING: { label: '待首单', color: 'orange' },
  QUALIFIED: { label: '已达标', color: 'green' },
  NOT_QUALIFIED: { label: '未达标', color: 'red' },
};

const Invites = () => {
  const { admin } = useAuth();
  const canManagePolicy = canManageInvitePolicy(admin?.role);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialInviterUserId = searchParams.get('inviterUserId') || '';
  const initialStatus = searchParams.get('status') || '';

  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    inviterUserId: initialInviterUserId,
    status: initialStatus,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    inviterUserId: initialInviterUserId,
    status: initialStatus,
  });
  const currentPage = pagination.current;
  const currentPageSize = pagination.pageSize;

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const result = await getInviteSummary();
      setSummary(result);
    } catch (error) {
      console.error('Failed to fetch invite summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getInvites({
        page: currentPage,
        size: currentPageSize,
        inviterUserId: appliedFilters.inviterUserId || undefined,
        status: appliedFilters.status || undefined,
      });
      setData(result.list);
      setPagination((prev) => ({ ...prev, total: result.total }));
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentPageSize, appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    const nextFilters = { ...filters };
    setAppliedFilters(nextFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));

    const params = {};
    if (nextFilters.inviterUserId) {
      params.inviterUserId = nextFilters.inviterUserId;
    }
    if (nextFilters.status) {
      params.status = nextFilters.status;
    }
    setSearchParams(params);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '邀请人',
      key: 'inviter',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/users?userId=${record.inviterUserId}`)}>
            {record.inviterNickname || `用户 ${record.inviterUserId}`}
          </Button>
          <span style={{ color: '#999' }}>ID: {record.inviterUserId}</span>
        </Space>
      ),
    },
    {
      title: '被邀请人',
      key: 'invitee',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/users?userId=${record.inviteeUserId}`)}>
            {record.inviteeNickname || `用户 ${record.inviteeUserId}`}
          </Button>
          <span style={{ color: '#999' }}>ID: {record.inviteeUserId}</span>
        </Space>
      ),
    },
    { title: '邀请码', dataIndex: 'inviteCode', width: 120 },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status) => {
        const item = statusMap[status] || { label: status, color: 'default' };
        return <Tag color={item.color}>{item.label}</Tag>;
      },
    },
    {
      title: '奖励',
      key: 'reward',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span>{record.rewardType || '-'}</span>
          <span style={{ color: '#999' }}>{record.rewardValue || '-'}</span>
        </Space>
      ),
    },
    {
      title: '奖励发放',
      dataIndex: 'rewardGranted',
      render: (value) => (
        <Tag color={value === 1 ? 'green' : 'default'}>
          {value === 1 ? '已发放' : '未发放'}
        </Tag>
      ),
    },
    {
      title: '累计订单',
      dataIndex: 'totalOrderAmount',
      render: (value) => `¥${(value || 0).toFixed(2)}`,
    },
    {
      title: '注册时间',
      dataIndex: 'registerTime',
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
      width: 160,
    },
    {
      title: '首单时间',
      dataIndex: 'firstOrderTime',
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
      width: 160,
    },
  ];

  return (
    <div>
      <h2>邀请管理</h2>

      {canManagePolicy && (
        <Alert
          showIcon
          type="info"
          icon={<SettingOutlined />}
          message="邀请权益已迁移到系统配置"
          description="邀请奖励与新用户权益统一在系统配置中维护，此页面专注于邀请关系和奖励记录。"
          action={<Link to="/admin/system-config#growth">前往系统配置</Link>}
          style={{ marginBottom: 20 }}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card loading={summaryLoading}>
            <Statistic title="总邀请关系" value={summary?.totalRelations || 0} />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={summaryLoading}>
            <Statistic title="已达标邀请" value={summary?.qualifiedCount || 0} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={summaryLoading}>
            <Statistic title="待发奖励" value={summary?.pendingCount || 0} valueStyle={{ color: '#d48806' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={summaryLoading}>
            <Statistic title="已发奖励" value={summary?.rewardGrantedCount || 0} valueStyle={{ color: '#ff2442' }} />
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="邀请人用户ID"
          value={filters.inviterUserId}
          onChange={(e) => setFilters((prev) => ({ ...prev, inviterUserId: e.target.value }))}
          style={{ width: 160 }}
        />
        <Select
          placeholder="状态"
          allowClear
          value={filters.status || undefined}
          onChange={(value) => setFilters((prev) => ({ ...prev, status: value || '' }))}
          style={{ width: 140 }}
          options={[
            { value: 'REGISTERED', label: '已注册' },
            { value: 'PENDING', label: '待首单' },
            { value: 'QUALIFIED', label: '已达标' },
            { value: 'NOT_QUALIFIED', label: '未达标' },
          ]}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          搜索
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => setPagination({ current: page, pageSize, total: pagination.total }),
        }}
      />
    </div>
  );
};

export default Invites;
